import { put, takeLatest } from 'redux-saga/effects';
import authSlice from 'store/modules/auth';
import sessionSlice from 'store/modules/session';
import uiSlice from 'store/modules/ui';
import session from 'api/graphql/session';
import { sessionListIdOnlyFields } from 'common/constants/query-fields/session';
import { userAvailableSessionsFields } from 'common/constants/query-fields/user';
import { createSnack, getUserFriendlyError } from 'common/utils/general';
import { buildSessionEvent } from 'common/utils/session';
import i18n from 'i18n';

/**
 * Counters sessions saga
 *
 * @param {*} action
 */
function* counters(action) {
  try {
    const response = yield session.counters(action.payload);
    const {
      data: { sessionCounters }
    } = response;

    yield put(sessionSlice.actions.countersSuccess(sessionCounters));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(sessionSlice.actions.countersError(message));
  }
}

/**
 * Create sessions saga
 *
 * @param {*} action
 */
function* create(action) {
  try {
    const response = yield session.create(action.payload);
    const {
      data: {
        createSession: {
          client_id: { available_sessions: availableSessions }
        }
      }
    } = response;

    yield put(sessionSlice.actions.createSuccess());
    yield put(authSlice.actions.updateAvailableSessionsCounter(availableSessions));
    // Refresca AMBOS contadores del usuario (available_sessions + personalized_sessions) para que el
    // chip del sidebar se actualice al instante (el chip muestra personalized_sessions, que la
    // mutación de create NO devuelve).
    yield put(authSlice.actions.me(userAvailableSessionsFields));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(sessionSlice.actions.createError(message));
  }
}

/**
 * Create multiple sessions saga (calendario multi-fecha).
 * Crea secuencialmente una sesión por cada payload; así `takeLatest` no cancela
 * (sería el caso si se despacharan N acciones `create` en bucle).
 *
 * @param {*} action  action.payload = array de payloads de sesión
 */
function* createBatch(action) {
  const payloads = Array.isArray(action.payload) ? action.payload : [];
  let availableSessions;
  // Se guarda la fecha y el MOTIVO de cada fallo, no solo cuántos: desde que el servidor rechaza
  // el solape de agenda del entrenador, "2 fechas no se pudieron crear" no le dice al admin cuáles
  // ni por qué, y se quedaba adivinando.
  const failures = [];

  for (let i = 0; i < payloads.length; i += 1) {
    try {
      const response = yield session.create(payloads[i]);
      availableSessions = response?.data?.createSession?.client_id?.available_sessions ?? availableSessions;
    } catch (ex) {
      const rawDate = payloads[i]?.start_date;
      const when = rawDate ? new Date(rawDate).toLocaleString() : `#${i + 1}`;
      failures.push({ when, reason: getUserFriendlyError(ex) });
    }
  }

  if (failures.length === payloads.length && payloads.length > 0) {
    // Con una sola fecha, el motivo del servidor es más útil que un genérico.
    const firstReason = failures[0]?.reason;
    yield put(
      sessionSlice.actions.createError(firstReason || i18n.t('general.unexpected', { ns: 'errors' }))
    );
    return;
  }

  yield put(sessionSlice.actions.createSuccess());
  if (availableSessions !== undefined) {
    yield put(authSlice.actions.updateAvailableSessionsCounter(availableSessions));
  }
  yield put(authSlice.actions.me(userAvailableSessionsFields));
  // Aviso si alguna fecha no se pudo crear (entrenador ocupado a esa hora, sin disponibilidad
  // ese día...). Se listan las fechas concretas y el motivo del servidor, para que el admin sepa
  // exactamente qué reagendar en vez de un contador mudo.
  if (failures.length > 0) {
    const detail = failures.map((f) => (f.reason ? `${f.when}: ${f.reason}` : f.when)).join(' · ');

    yield put(
      uiSlice.actions.enqueueSnackbar(
        createSnack({
          key: `session-batch-partial-${Date.now()}`,
          variant: 'warning',
          message: `${i18n.t('session.batchPartial', {
            ns: 'notifications',
            count: failures.length,
            defaultValue: '{{count}} fecha(s) no se pudieron crear.'
          })} ${detail}`.trim()
        })
      )
    );
  }
}

/**
 * Dashboard sessions saga
 *
 * @param {*} action
 */
function* dashboard(action) {
  try {
    const response = yield session.getDashboardData(action.payload);

    const {
      data: { sessionDashboard }
    } = response;

    yield put(sessionSlice.actions.dashboardSuccess(sessionDashboard));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(sessionSlice.actions.dashboardError(message));
  }
}

/**
 * Find sessions saga
 *
 * @param {*} action
 */
function* find(action) {
  try {
    const response = yield session.find(action.payload);
    const {
      data: { session: currentSession }
    } = response;

    yield put(sessionSlice.actions.findSuccess(currentSession));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(sessionSlice.actions.findError(message));
  }
}

/**
 * Force delete sessions saga
 *
 * @param {*} action
 */
function* forceDelete(action) {
  try {
    const response = yield session.forceDelete(action.payload);
    const {
      data: { deleteSessions }
    } = response;

    yield put(sessionSlice.actions.forceDeleteSuccess());
    yield put(authSlice.actions.incrementAvailableSessionsCounter(deleteSessions.length));
    yield put(authSlice.actions.me(userAvailableSessionsFields));

    const snack = createSnack({
      key: 'delete-session-success',
      variant: 'success',
      message: i18n.t('session.delete', {
        ns: 'notifications',
        count: deleteSessions.length
      })
    });

    yield put(uiSlice.actions.enqueueSnackbar(snack));
  } catch (ex) {
    // Antes el fallo se tragaba en silencio (nadie lee `status.general`), así que el usuario
    // veía "no pasa nada" al borrar. El motivo real (p. ej. permisos) va al snackbar.
    const message = getUserFriendlyError(ex);
    const snack = createSnack({
      key: 'delete-session-error',
      variant: 'error',
      message
    });

    yield put(sessionSlice.actions.forceDeleteError(message));
    yield put(uiSlice.actions.enqueueSnackbar(snack));
  }
}

/**
 * Get sessions in range saga
 *
 * @param {*} action
 */
function* getSessionsInRange(action) {
  try {
    const { dateRange, userId } = action.payload;
    const response = yield session.listAll({
      fields: sessionListIdOnlyFields,
      filters: { client_id: userId, happen_between: dateRange }
    });

    const {
      data: { sessionsAll }
    } = response;

    const sessionsInRangeObject = {
      exists: sessionsAll.length > 0,
      startDate: dateRange.start_date,
      endDate: dateRange.end_date
    };

    yield put(sessionSlice.actions.getSessionsInRangeSuccess(sessionsInRangeObject));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(sessionSlice.actions.getSessionsInRangeError(message));
  }
}

/**
 * List sessions saga
 *
 * @param {*} action
 */
function* list(action) {
  try {
    const {
      payload: { filters = {}, pagination, orderBy = null }
    } = action;
    const response = yield session.list({ filters, pagination, orderBy });
    const {
      data: {
        sessions: { data, paginatorInfo }
      }
    } = response;

    yield put(sessionSlice.actions.listSuccess(data));
    yield put(
      sessionSlice.actions.updatePaginatorInfo({
        hasMorePages: paginatorInfo.hasMorePages,
        lastPage: paginatorInfo.lastPage,
        total: paginatorInfo.total
      })
    );
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(sessionSlice.actions.listError(message));
  }
}

/**
 * List all sessions saga
 *
 * @param {*} action
 */
function* listAll(action) {
  try {
    const { fields, filters } = action.payload;
    const response = yield session.listAll({ fields, filters });

    const {
      data: { sessionsAll }
    } = response;

    yield put(sessionSlice.actions.listAllSuccess(sessionsAll));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(sessionSlice.actions.listAllError(message));
  }
}

/**
 * List event sessions saga
 *
 * @param {*} action
 */
function* listEvent(action) {
  try {
    const { fields, filters } = action.payload;
    const response = yield session.listAll({ fields, filters });

    const {
      data: { sessionsAll }
    } = response;

    const calendarEvents = sessionsAll.map((event) => buildSessionEvent(event, true));
    yield put(sessionSlice.actions.listEventSuccess(calendarEvents));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(sessionSlice.actions.listEventError(message));
  }
}

/**
 * Update session saga
 *
 * @param {*} action
 */
function* update(action) {
  try {
    const snack = createSnack({
      key: 'update-session-notification-success',
      variant: 'success',
      message: i18n.t('session.update', { ns: 'notifications' })
    });

    yield session.update(action.payload);

    yield put(sessionSlice.actions.updateSuccess());
    yield put(uiSlice.actions.enqueueSnackbar(snack));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(sessionSlice.actions.updateError(message));
  }
}

/**
 * Watch session
 *
 * @export
 */
export default function* watchSession() {
  yield takeLatest(sessionSlice.actions.counters, counters);
  yield takeLatest(sessionSlice.actions.create, create);
  yield takeLatest(sessionSlice.actions.createBatch, createBatch);
  yield takeLatest(sessionSlice.actions.dashboard, dashboard);
  yield takeLatest(sessionSlice.actions.find, find);
  yield takeLatest(sessionSlice.actions.forceDelete, forceDelete);
  yield takeLatest(sessionSlice.actions.getSessionsInRange, getSessionsInRange);
  yield takeLatest(sessionSlice.actions.list, list);
  yield takeLatest(sessionSlice.actions.listAll, listAll);
  yield takeLatest(sessionSlice.actions.listEvent, listEvent);
  yield takeLatest(sessionSlice.actions.update, update);
}
